import type { FC } from "react";
import { useEffect, useState } from "react";

import { ResponsiveImage } from "@ui/components/ResponsiveImage";
import { useCurrentBreakpoint } from "@ui/hooks/useCurrentBreakpoint";
import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import type { TrackingEventsAdditionalData } from "@jti/tracker";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import type { FlavorFinderQuizResults } from "../../types/FlavorFinderTypes";
import { FlavorFinderResultCardSpec } from "../ResultsCardSpec/FlavorFinderResultsCardSpec";

type FlavorFinderResultProps = {
  result: FlavorFinderQuizResults;
  isHiglighted?: boolean;
  intensityLabel?: string;
  flavorLabel?: string;
  coolingLabel?: string;
  index: number;
  onClick: (index: number) => void;
};

export const FlavorFinderResultCard: FC<FlavorFinderResultProps> = ({
  result,
  isHiglighted = false,
  intensityLabel: intensityLabelProp,
  flavorLabel: flavorLabelProp,
  coolingLabel: coolingLabelProp,
  onClick,
  index,
}) => {
  const { __ } = useTranslations();
  const intensityLabel = intensityLabelProp ?? __("flavorfinder.intensity.label");
  const flavorLabel = flavorLabelProp ?? __("flavorfinder.power.label");
  const coolingLabel = coolingLabelProp ?? __("flavorfinder.cooling.label");

  const {
    sku,
    brand,
    title,
    description,
    intensity,
    flavor,
    cooling,
    bgGradientAngle,
    bgGradientColorFrom,
    bgGradientColorTo,
    regularImageDesktop,
    regularImageTablet,
    regularImageMobile,
    exposedImageDesktop,
    exposedImageTablet,
    exposedImageMobile,
    splashImageTablet,
    splashImageDesktop,
  } = result;

  const { isSmall } = useCurrentBreakpoint();
  const [gradientBackground, setGradientBackground] = useState("");

  useEffect(() => {
    setGradientBackground(`linear-gradient(${bgGradientAngle}, ${bgGradientColorFrom}, ${bgGradientColorTo})`);
  }, [isSmall, bgGradientAngle, bgGradientColorFrom, bgGradientColorTo]);

  const renderBackroundImages = () => {
    return !isSmall ? (
      <div className={"flavorfinder__result-background"}>
        <ResponsiveImage
          largeSrc={splashImageDesktop}
          mediumSrc={splashImageTablet}
          className="responsive-image__container"
        />
      </div>
    ) : null;
  };

  const renderCardImage = () => {
    return (
      <div className={"flavorfinder__result-card-image"}>
        {isHiglighted ? (
          <ResponsiveImage
            largeSrc={exposedImageDesktop}
            mediumSrc={exposedImageTablet}
            smallSrc={exposedImageMobile}
            className="responsive-image__container"
          />
        ) : (
          <ResponsiveImage
            largeSrc={regularImageDesktop}
            mediumSrc={regularImageTablet}
            smallSrc={regularImageMobile}
            className="responsive-image__container"
          />
        )}
      </div>
    );
  };

  const handleCardClick = () => {
    if (isHiglighted) {
      return;
    }
    const itemName = `${brand} ${title}`;
    const selectedItemData: TrackingEventsAdditionalData[TrackingEvents.TrackingSelectItem] = {
      currency: "N/A",
      value: "N/A",
      ecommerce: {
        items: [
          {
            item_id: sku || itemName,
            currency: "N/A",
            price: "N/A",
            discount: "N/A",
            quantity: 1,
            index: 0,
            item_brand: brand,
            item_category: "N/A",
            item_category2: "N/A",
            item_name: itemName,
            item_color: bgGradientColorFrom,
            item_flavor: String(flavor),
            item_strength: String(intensity),
            item_coupon: "N/A",
            item_coupon_type: "N/A",
            item_list_id: "N/A",
            item_list_name: "N/A",
            item_rewards: "N/A",
            item_sku: sku,
            carton_format: "N/A",
            carton_quantity: "N/A",
            purchase_type: "N/A",
          },
        ],
      },
    };

    dispatchTrackingEvent(TrackingEvents.TrackingSelectItem, selectedItemData);
    onClick(index);
  };

  const resultCardStyle = { background: gradientBackground };

  return (
    <button
      className={classNames("flavorfinder__result", { "flavorfinder__result--highlighted": isHiglighted })}
      onClick={handleCardClick}
      data-testid="FlavorfinderResultsCard"
    >
      {renderBackroundImages()}
      <div
        className="flavorfinder__result-card"
        data-sku={sku}
        data-testid="flavor-finder-result-card"
        style={resultCardStyle}
      >
        {renderCardImage()}
        <div className="flavorfinder__result-card-text">
          <p className="flavorfinder__result-card-brand">{brand}</p>
          <p className="flavorfinder__result-card-title">{title}</p>
          <span className="flavorfinder__result-card-description">{description}</span>
        </div>
        <div className="flavorfinder__result-card-specs">
          {flavor > 0 && <FlavorFinderResultCardSpec power={flavor} label={flavorLabel} />}
          {intensity > 0 && <FlavorFinderResultCardSpec power={intensity} label={intensityLabel} />}
          {cooling > 0 && <FlavorFinderResultCardSpec power={cooling} label={coolingLabel} />}
        </div>
      </div>
    </button>
  );
};
